import { Button } from '@efexteam/components';
import { Actions, Subjects, usePermission } from '@efexteam/permission-manager';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import DataLoadingWrapper from '../../components/common/DataLoadingWrapper';
import OutletTableSkeleton from '../../components/common/Skeleton/OutletTableSkeleton';
import { DevLabelEnum } from '../../configs/language/developersTranslations';
import useBoolean from '../../hooks/useBoolean';
import { useAppDispatch } from '../../store';
import CreateCredentialModal from './components/CreateCredentialModal/CreateCredentialModal';
import CredentialsTable from './components/CredentialsTable/CredentialsTable';
import DeleteCredentialModal from './components/DeleteCredentialModal/DeleteCredentialModal';
import { ICredentialResponse } from './Credentials.interface';
import CredentialsStyles from './Credentials.styles';
import { fetchCredentials } from './store';
import selectCredentialState from './store/selectors';

const Credentials = () => {
  const { can } = usePermission();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const credentialState = useSelector(selectCredentialState);
  const [credentialSelected, setCredentialSelected] =
    useState<ICredentialResponse | null>();
  const [
    isModalDeleteOpen,
    { setFalse: hideModalDelete, setTrue: showModalDelete },
  ] = useBoolean(false);
  const [
    isModalInsertOpen,
    { setFalse: hideModalInsert, setTrue: showModalInsert },
  ] = useBoolean(false);

  useEffect(() => {
    dispatch(fetchCredentials());
  }, [dispatch]);

  const handleDelete = (element: ICredentialResponse) => {
    showModalDelete();
    setCredentialSelected(element);
  };

  const openNewCredential = () => {
    setCredentialSelected(null);
    showModalInsert();
  };

  const renderContent = () => (
    <div className={CredentialsStyles.container}>
      <div className={CredentialsStyles.content}>
        <div className={CredentialsStyles.header}>
          <div>
            <div className={CredentialsStyles.boxTitle}>
              {t(DevLabelEnum.DEV_TITLE)}
            </div>
            <div className={CredentialsStyles.subTitle}>
              {t(DevLabelEnum.DEV_SUBTITLE)}
            </div>
            <div className={CredentialsStyles.subTitle}>
              {t(DevLabelEnum.DEV_SUBTITLE_TWO)}
            </div>
          </div>
          <Button appearance="primary" onClick={openNewCredential}>
            {t(DevLabelEnum.DEV_ADD)}
          </Button>
        </div>
        <CredentialsTable
          credentials={credentialState.data ?? []}
          handleDelete={handleDelete}
        />
      </div>
    </div>
  );

  const canAccessDevelopers =
    can(Actions.MANAGE, Subjects.DEVELOPER) ||
    can(Actions.READ, Subjects.DEVELOPER);

  if (!canAccessDevelopers) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div>
      <DataLoadingWrapper
        loadingData={credentialState.loading}
        customLoader={<OutletTableSkeleton />}
      >
        {renderContent()}
      </DataLoadingWrapper>

      {isModalInsertOpen && (
        <CreateCredentialModal
          open={isModalInsertOpen}
          onClose={hideModalInsert}
        />
      )}

      {isModalDeleteOpen && credentialSelected && (
        <DeleteCredentialModal
          open={isModalDeleteOpen}
          credential={credentialSelected}
          onClose={hideModalDelete}
        />
      )}
    </div>
  );
};

export default Credentials;
